import React from 'react'
import moment from 'moment-timezone'
import { queryCache, usePaginatedQuery } from 'react-query'
import copy from 'copy-to-clipboard'
import { SearchOutlined } from '@ant-design/icons';
import { Col, Row, Table, Tag, Button, message, Tooltip } from 'antd'

import StatusTranslation from '../../../data/status'
import RepositoryLineAction from './RepositoryLineAction'
import BoletoFilter from './BoletoFilter'
import BoletoDetail from './BoletoDetail'
import EmailModal from './EmailModal'
import BoletoService from 'services/boleto'

moment.tz.setDefault("America/Sao_Paulo")

const getStatusColor = status => {
  const statusColors = {
    PENDING: '#ff9800',
    REJECTED: '#f44336',
    REGISTRADO: '#2196f3',
    TERMINATED: '#b4b4b4',
    LIQUIDADO: '#4caf50',
    'MANUALLY PAID': '#4caf50',
    'PENDING TERMINATION': '#ff9800',
    'PENDING MANUAL PAYMENT CONFIRMATION': '#ff9800',
    'OVERDUE': '#b71c1c'
  }

  return statusColors[status]
}

const translateStatus = status => {
  const filtered = StatusTranslation.filter(item => {
    return item.status === status
  })

  if (filtered.length === 1) {
    return filtered.pop().translate
  }

  return status
}

function Repository(props) {
  const [detailVisible, setDetailVisible] = React.useState(false)
  const [filterVisible, setFilterVisible] = React.useState(false)
  const [filters, setFilters] = React.useState({ page: 1 })
  const [emailModalVisible, setEmailModalVisible] = React.useState(false)
  const [selectedBoleto, setSelectedBoleto] = React.useState(null)
  const [boletoDetail, setBoletoDetail] = React.useState(null)
 
  const fetchBoletos = React.useCallback(async (key, filters) => {
    const { data } = await BoletoService.list(filters)

    return data
  }, [])

  const {
    status,
    resolvedData,
    latestData,
  } = usePaginatedQuery(['boletos', filters], fetchBoletos, {})

  React.useEffect(() => {
    if (latestData && latestData.hasMore) {
      queryCache.prefetchQuery(['boletos', filters.page + 1], fetchBoletos)
    }
  }, [latestData, fetchBoletos, filters])

  function handleTableChange(pagination, filtro, sorter){
    if (sorter && sorter.field) {
      const dr = sorter.order === 'ascend' ? '' : '-'
      setFilters({ ...filters, page: pagination.current, sort: `${dr}${sorter.field}`})
      return
    }
    setFilters({ ...filters, page: pagination.current})
  }

  function handleDownload(boleto) {
    return () => {
      props.onDownload(boleto.idintegracao)
    }
  }

  function handleResend(boleto) {
    return () => {
      showEmailModal(boleto.idintegracao)
    }
  }

  function handleCopiarLinhaDigitavel({ TituloLinhaDigitavel }) {
    return async () => {
      try {
        copy(TituloLinhaDigitavel);
          message.info('Linha digitável copiada')
      } catch (error) {
        console.error(error)
      }
    }
  }

  function handleFilter() {
    setFilterVisible(true)
  }

  function closeFilter() {
    setFilterVisible(false)
  }

  function handleDetail(boleto) {
    return () => {
      setBoletoDetail(boleto.idintegracao)
      setDetailVisible(true)
    }
  }

  function closeDetail() {
    setDetailVisible(false)
  }

  function showEmailModal(idintegracao) {
    setSelectedBoleto(idintegracao)
    setEmailModalVisible(true)
  }

  function handleCancel() {
    setEmailModalVisible(false)
    setSelectedBoleto(null)
  }

  function GetFiltersAndQuery(values) {
    setFilters(values)
  }

  const actionsGroup = [
    {
      title: "Baixar pdf",
      onExecute: handleDownload,
      icon: "file-pdf",
      disabled: situacao => ['LIQUIDADO', 'BAIXADO'].includes(situacao)
    },
    {
      title: "Reenviar por email",
      onExecute: handleResend,
      icon: "mail",
      disabled: situacao => ['LIQUIDADO', 'BAIXADO'].includes(situacao)
    },
    {
      title: "Copiar linha digitável",
      onExecute: handleCopiarLinhaDigitavel,
      icon: "barcode",
      disabled: situacao => ['LIQUIDADO', 'BAIXADO'].includes(situacao)
    },
    {
      title: "Detalhes do título",
      onExecute: handleDetail,
      icon: "select",
      disabled: () => false
    }
  ]

  const columns = [
    {
      title: 'Nome do cedente',
      dataIndex: 'razaosocial',
      key: 'razaosocial',
      align: 'center',
      sorter : true,
      sortDirections: ['descend', 'ascend', 'descend'],
    },
    {
      title: 'CPF/CNPJ do cedente',
      dataIndex: 'cpf_cnpj',
      key: 'cpf_cnpj',
      align: 'center',
      sorter : true,
      sortDirections: ['descend', 'ascend','descend'],
    },
    {
      title: 'Nosso número',
      dataIndex: 'TituloNossoNumero',
      key: 'TituloNossoNumero',
      align: 'center',
      sorter : true,
      sortDirections: ['descend', 'ascend','descend'],
    },
    {
      title: 'Data de emissão',
      dataIndex: 'TituloDataEmissao',
      key: 'TituloDataEmissao',
      sorter : true,
      sortDirections: ['descend', 'ascend','descend'],
      align: 'center',
      render: text => {
        return moment(text).format('DD/MM/YYYY')
      }
    },
    {
      title: 'Data de vencimento',
      dataIndex: 'TituloDataVencimento',
      key: 'TituloDataVencimento',
      sorter : true,
      sortDirections: ['descend', 'ascend','descend'],
      align: 'center',
      render: text => {
        return moment(text).format('DD/MM/YYYY')
      }
    },
    {
      title: 'Valor',
      dataIndex: 'TituloValor',
      key: 'TituloValor',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend','descend'],
      render: text => {
        return `R$ ${parseFloat(text)
          .toFixed(2)
          .replace('.', ',')}`
      }
    },
    {
      title: 'Situação',
      dataIndex: 'situacao',
      key: 'situacao',
      align: 'center',
      render: (text, record, index) => {
        return <Tag color={getStatusColor(text)}>{translateStatus(text)}</Tag>
      }
    },
    {
      title: 'Ações',
      dataIndex: '',
      align: 'center',
      key: 'actions',
      render: (text, record) => {
        return <RepositoryLineAction
          actions={actionsGroup.map(actionItem => {
            const action = {
              ...actionItem,
              onExecute: actionItem.onExecute(record),
              disabled: actionItem.disabled(text.situacao)
            }
            return action
          })}
        />
      }
    }
  ]

  return [
    <EmailModal
      idintegracao={selectedBoleto}
      visible={emailModalVisible}
      onCancel={handleCancel}
    />,
    <div className="text-right">
      <Tooltip
        placement="leftTop"
        title="Filtrar Boletos"
      >
        <Button
          type="primary"
          shape="circle"
          icon={<SearchOutlined />}
          size="large"
          onClick={handleFilter}
        />
      </Tooltip>
      <BoletoFilter
        showFilter={filterVisible}
        executeOnClose={closeFilter}
        createFilter={GetFiltersAndQuery}
      />
      {detailVisible
        ? <BoletoDetail
          showDetail={false}
          executeOnClose={closeDetail}
          idintegracao={boletoDetail}
        />
        : <div></div>
      }
      <Row>
        <Col span={24}>
          <Table
            columns={columns}
            loading={status === 'loading'}
            size={'middle'}
            rowKey="idintegracao"
            pagination={{
              current: filters.page,
              limit: 10,
              total: resolvedData ? resolvedData.count : 0
            }}
            dataSource={resolvedData ? resolvedData.boletos : []}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </div>
  ];
}

export default Repository
